import React from "react";
import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";

const cardSx = {
  p: 3,
  borderRadius: 1.5,
  boxShadow: (theme) => theme.palette.shadow.statCard,
  border: (theme) => `1px solid ${theme.palette.border.light}`,
  bgcolor: "background.paper",
  height: "100%",
};

function formatSar(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `SAR ${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `SAR ${(x / 1_000).toFixed(1)}K`;
  return `SAR ${new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(x)}`;
}

const FALLBACK_COLORS = [
  "#0d9488", "#3b82f6", "#ef4444", "#22c55e", "#8b5cf6",
  "#f97316", "#06b6d4", "#ec4899", "#a3e635", "#facc15",
];

const InvestorAssetDistribution = ({ distribution = [], isLoading = false }) => {
  const data = distribution.map((item, i) => ({
    id: i,
    label: item.categoryName,
    value: Number(item.value) || 0,
    color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));

  return (
    <Paper elevation={0} sx={cardSx}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "text.primary" }}>
        Asset Distribution
      </Typography>
      <Typography variant="body2" sx={{ color: "text.muted", mt: 0.25, mb: 2 }}>
        Portfolio breakdown by category
      </Typography>

      {isLoading ? (
        <Stack alignItems="center" spacing={2}>
          <Skeleton variant="circular" width={180} height={180} />
          <Stack spacing={1} width="100%">
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="text" height={24} />)}
          </Stack>
        </Stack>
      ) : data.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No distribution data available.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <PieChart
              series={[{ data, innerRadius: 55, outerRadius: 90, paddingAngle: 2, cornerRadius: 4 }]}
              width={220}
              height={220}
              slotProps={{ legend: { hidden: true } }}
            />
          </Box>

          <Stack spacing={1.25} sx={{ mt: 2 }}>
            {data.map((item) => (
              <Stack key={item.id} direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: "text.primary", fontSize: 13 }}>
                    {item.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 700, fontSize: 13 }}>
                  {formatSar(item.value)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default InvestorAssetDistribution;
